import { Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useUpcomingEvents } from '@/hooks/useEvents';
import { format } from 'date-fns';

export function UpcomingEventsWidget() {
  const { data: events, isLoading } = useUpcomingEvents(4);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Upcoming Events
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="h-16 w-16 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Upcoming Events
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">No upcoming events at the moment.</p>
          <Button variant="outline" size="sm" asChild className="mt-4">
            <Link to="/events">Browse Events</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          Upcoming Events
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
        {events.map((event) => (
          <Link
            key={event.id}
            to={`/events/${event.slug}`}
            className="flex gap-3 group hover:bg-muted/50 rounded-lg p-2 -mx-2 transition-colors"
          >
            {event.image_url ? (
              <img
                src={event.image_url}
                alt={event.title}
                loading="lazy"
                decoding="async"
                className="h-16 w-16 object-cover rounded-lg"
              />
            ) : (
              <div className="h-16 w-16 bg-muted rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm line-clamp-1 group-hover:text-primary transition-colors">
                {event.title}
              </h4>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                <Calendar className="h-3 w-3" />
                {format(new Date(event.start_date), 'MMM d, yyyy')}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {event.event_type === 'online' ? 'Online' : event.event_type === 'in_person' ? 'In-Person' : 'Hybrid'}
                </Badge>
                {event.is_free && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    Free
                  </Badge>
                )}
              </div>
            </div>
          </Link>
        ))}
        </div>

        
        <Button variant="ghost" size="sm" asChild className="w-full mt-2">
          <Link to="/events" className="flex items-center gap-2">
            View All Events
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
